import Card from "components/molecules/Card";
import { useCurrentAnnualCarbonEmissionsCard } from "queries/Reports/Baseline/useCurrentAnnualCarbonEmissionsCard";
import { useCurrentAnnualEnergyConsumptionCard } from "queries/Reports/Baseline/useCurrentAnnualEnergyConsumptionCard";
import { useCurrentAnnualEnergyCostCard } from "queries/Reports/Baseline/useCurrentAnnualEnergyCostCard";
import { useCurrentAnnualEndUseChart } from "queries/Reports/Baseline/useCurrentAnnualEndUseChart";
import { buildSankeyData } from "components/organisms/Charts/EndUseSankey";
import SankeyChart from "pages/BuildingPage/VirtualAudit/SankeyChart";
import styles from "pages/BuildingPage/VirtualAudit/VirtualAuditPage.module.scss";

interface PropertyBaselineSectionProps {
    filtersetUid: string;
}

function PropertyBaselineSection({ filtersetUid }: PropertyBaselineSectionProps) {
    const { data: carbonCard, isLoading: carbonLoading } = useCurrentAnnualCarbonEmissionsCard(filtersetUid);
    const { data: energyCard, isLoading: energyLoading } = useCurrentAnnualEnergyConsumptionCard(filtersetUid);
    const { data: energyCostCard, isLoading: costLoading } = useCurrentAnnualEnergyCostCard(filtersetUid);
    const { data: endUseChart } = useCurrentAnnualEndUseChart(filtersetUid);

    const carbonCardData =
        carbonCard != null
            ? {
                  header: {
                      displayInfoKey: "totalCarbonEmissions",
                      value: carbonCard.totalCarbonEmissions ?? "—",
                      title: "Total annual carbon emissions",
                  },
                  sections: [
                      [
                          {
                              displayInfoKey: "averageEmissionsIntensity",
                              value: carbonCard.averageEmissionsIntensity ?? "—",
                              title: "Average intensity",
                          },
                      ],
                  ],
              }
            : undefined;

    const energyCardData =
        energyCard != null
            ? {
                  header: {
                      displayInfoKey: "totalEnergyConsumption",
                      value: energyCard.totalEnergyConsumption ?? "—",
                      title: "Total annual energy consumption",
                  },
                  sections: [
                      [
                          {
                              displayInfoKey: "averageEnergyIntensity",
                              value: energyCard.averageEnergyIntensity ?? "—",
                              title: "Average intensity",
                          },
                      ],
                  ],
              }
            : undefined;

    const costCardData =
        energyCostCard != null && energyCostCard.totalEnergyCost > 0
            ? {
                  header: {
                      displayInfoKey: "totalEnergyCost",
                      value: energyCostCard.totalEnergyCost,
                      title: "Total energy cost",
                  },
                  sections: [
                      [
                          {
                              displayInfoKey: "landlordEnergyCost",
                              value: energyCostCard.landlordEnergyCost,
                              title: "Landlord energy cost",
                          },
                          {
                              displayInfoKey: "tenantEnergyCost",
                              value: energyCostCard.tenantEnergyCost,
                              title: "Tenant energy cost",
                          },
                      ],
                  ],
              }
            : undefined;

    return (
        <div>
            <h2 className={styles.sectionTitle}>Baseline Performance</h2>

            <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1fr", gap: "1.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <Card data={carbonCardData} loading={carbonLoading} cardTitle="Total annual carbon emissions" />
                    <Card data={energyCardData} loading={energyLoading} cardTitle="Total annual energy consumption" />
                    {(costLoading || costCardData != null) && (
                        <Card data={costCardData} loading={costLoading} cardTitle="Total energy cost" />
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                    {endUseChart && (
                        <>
                            <SankeyChart
                                title="Energy end use breakdown"
                                data={buildSankeyData(endUseChart.energyConsumption ?? [])}
                            />
                            <SankeyChart
                                title="Emissions end use breakdown"
                                data={buildSankeyData(endUseChart.carbonEmissions ?? [])}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PropertyBaselineSection;
